import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useMemo,
} from 'react';
import settingsService from '../services/settingsService';
import { useRealtimeSync } from '../hooks/useRealtimeSync';
import {
    CASH_DISCOUNT_PERCENT as HARDCODED_FALLBACK,
    getProductDiscount as resolveProductDiscount,
} from '../utils/cashPricing';

/**
 * DiscountContext
 *
 * Provides the live cash discount percentage fetched from the backend.
 * While loading, `discountPercent` is null and `isDiscountLoading` is true.
 * On error or when the backend returns no value, it falls back to the
 * hardcoded constant so the UI never shows broken prices.
 *
 * NOTE: Full functionality requires the backend endpoint:
 *   GET /api/ecommerce/settings
 *   Response: { success: true, data: { settings: { cash_discount_percent: 5 } } }
 */
const DiscountContext = createContext(null);

export function DiscountProvider({ children }) {
    const [discountPercent, setDiscountPercent] = useState(null);  // null = not yet loaded
    const [isDiscountLoading, setIsDiscountLoading] = useState(true);
    const [discountError, setDiscountError] = useState(null);

    const loadSettings = useCallback(async (showLoading = true) => {
        try {
            if (showLoading) setIsDiscountLoading(true);
            setDiscountError(null);
            const settings = await settingsService.getPublicSettings();

            const raw = settings?.cash_discount_percent;
            const parsed = Number(raw);

            if (Number.isFinite(parsed) && parsed >= 0 && parsed <= 100) {
                setDiscountPercent(parsed);
            } else {
                setDiscountPercent(HARDCODED_FALLBACK);
            }
        } catch (err) {
            console.warn(
                '[DiscountContext] Failed to fetch settings, using hardcoded fallback:',
                err?.message || err
            );
            setDiscountError(err?.message || 'Failed to load discount settings');
            setDiscountPercent(HARDCODED_FALLBACK);
        } finally {
            if (showLoading) setIsDiscountLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSettings(true);
    }, [loadSettings]);

    // Real-time synchronization: update discount percentages when settings or prices change
    useRealtimeSync('settings', useCallback(() => {
        loadSettings(false);
    }, [loadSettings]));

    useRealtimeSync('prices', useCallback(() => {
        loadSettings(false);
    }, [loadSettings]));

    const value = useMemo(
        () => ({
            /**
             * The resolved cash discount percentage (number).
             * null while loading for the first time.
             * Falls back to HARDCODED_FALLBACK on API error.
             */
            discountPercent,

            /** True only during the initial fetch, before any value is known */
            isDiscountLoading,

            /** Non-null only if the API call failed */
            discountError,

            /**
             * Convenience: the resolved value to use in calculations.
             * Returns the hardcoded fallback while loading so math never
             * breaks on the first render.
             */
            effectiveDiscount: discountPercent ?? HARDCODED_FALLBACK,

            /**
             * Helper to resolve the discount percentage for a specific product,
             * taking product-specific discount first, falling back to effective store discount.
             */
            getProductDiscount: (product) =>
                resolveProductDiscount(product, discountPercent ?? HARDCODED_FALLBACK),
        }),
        [discountPercent, isDiscountLoading, discountError]
    );

    return (
        <DiscountContext.Provider value={value}>
            {children}
        </DiscountContext.Provider>
    );
}

/**
 * Hook to access the live cash discount.
 *
 * Usage:
 *   const { effectiveDiscount, isDiscountLoading } = useDiscount();
 */
export function useDiscount() {
    const ctx = useContext(DiscountContext);
    if (!ctx) {
        throw new Error('useDiscount must be used inside <DiscountProvider>');
    }
    return ctx;
}

export default DiscountContext;
